<template>
  <div>
    <v-data-table :headers="headers" :items="users" sort-by="UserId" class="elevation-1">
      <!-- datatable header -->
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-spacer></v-spacer>

          <v-dialog v-model="dialog" max-width="500px">
            <template v-slot:activator="{ on }">
              <v-btn color="primary" dark class="mb-2" v-on="on" @click="openCreateUserDialog()" v-if="canManageAllUsers">Créer un utilisateur</v-btn>
            </template>

            <v-card>
              <v-card-text class="pt-5">
                <v-form ref="userForm">
                  <!-- Role -->
                  <v-select
                    :items="roles"
                    item-text="name"
                    item-value="name"
                    :menu-props="{ top: true, offsetY: true }"
                    v-model="editedItem.roles"
                    label="Rôles"
                    multiple
                    :rules="[rules.required, rules.roleRequired]"
                  />

                  <!-- firstname -->
                  <v-text-field v-model.trim="editedItem.firstName" label="Prénom" :rules="[rules.required, rules.counter]" counter maxlength="60" />

                  <!-- lastname -->
                  <v-text-field v-model.trim="editedItem.lastName" label="Nom" :rules="[rules.required, rules.counter]" counter maxlength="60" />

                  <!-- email -->
                  <v-text-field v-model.trim="editedItem.email" label="Email" :rules="[rules.required, rules.validEmail]" />

                  <!-- If we are creating a new User -->
                  <div>
                    <!-- Password -->
                    <v-text-field
                      v-model.trim="editedItem.newPassword"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      :rules="[rules.validPassword]"
                      :type="showPassword ? 'text' : 'password'"
                      name="input-10-1"
                      label="Nouveau mot de passe"
                      @click:append="showPassword = !showPassword"
                      v-click-outside="onClickOutsidePasswordField"
                    ></v-text-field>

                    <!-- Password Confirmation -->
                    <v-text-field
                      v-model.trim="editedItem.passwordConfirm"
                      :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                      :rules="[rules.requiredIfNewPassword(editedItem.passwordConfirm, editedItem.newPassword), rules.confirmPassword(editedItem.passwordConfirm, editedItem.newPassword)]"
                      :type="showPassword ? 'text' : 'password'"
                      name="input-10-1"
                      label="Confirmation du nouveau mot de passe"
                      @click:append="showPassword = !showPassword"
                    ></v-text-field>
                  </div>

                  <!-- enable -->
                  <v-checkbox v-model="editedItem.isEnabled" label="Actif" />
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn color="blue darken-1" text @click="closeDialog()"> Annuler </v-btn>

                <v-btn color="blue darken-1" text @click="submitForm(editedItem)"> Enregistrer </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <!-- roles column -->
      <template v-slot:[`item.roles`]="{ item }">
        <v-chip dark color="light-blue darken-3" v-for="role in item.roles" :key="role">
          {{ role }}
        </v-chip>
      </template>

      <!-- federations column -->
      <template v-slot:[`item.federations`]="{ item }">
        <v-chip dark color="red darken-4" v-for="federation in item.federations" :key="federation.id">
          {{ federation.name }}
        </v-chip>
      </template>

      <!-- disciplines column -->
      <template v-slot:[`item.disciplines`]="{ item }">
        <v-chip dark color="teal darken-1" v-for="discipline in item.disciplines" :key="discipline.id">
          {{ discipline.name }}
        </v-chip>
      </template>

      <!-- teams column -->
      <template v-slot:[`item.teams`]="{ item }">
        <v-chip dark color="green darken-1" v-for="team in item.teams" :key="team.id">
          {{ team.name }}
        </v-chip>
      </template>

      <!-- Actions column -->
      <template v-slot:[`item.action`]="{ item }" v-if="canManageAllUsers">
        <v-icon small class="mr-2" @click="openEditUserDialog(item)">edit</v-icon>
        <v-icon small class="mr-2" @click="(e) => deleteUser(e, item.id)">delete</v-icon>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { getUsersListAsync, createUserAsync, updateUserAsync, deleteUserAsync, getRolesListAsync } from "@/api/accountApi";
import { VDataTable } from "vuetify/lib";
import store from "@/store";
import { validate as isValidUUID } from "uuid";
import PermissionsService from "@/services/PermissionsService";

import _ from "lodash";

export default {
  components: {
    VDataTable,
  },

  data: () => ({
    dialog: false,
    rules: {
      required: (value) => !!value || "Requis.",
      roleRequired: (value) => value && value.length ? true : "Requis.",
      requiredIfNewPassword: (value, newPassword) => newPassword && !value ? "Requis." : true,
      counter: (value) => (value ? value.length <= 60 : "Max 60 caractères"),
      validPassword: (value) => (!value || value.length == 0 || value.length >= 5 ? true : "Min 5 caractères"),
      confirmPassword: (confirm, newPassword) => (confirm === newPassword ? true : "Les mots de passes doivent être identiques"),
      validEmail: (value) => !value || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value) || "E-mail invalide",
    },
    headers: [
      // { text: "Id", value: "id", sortable: true },
      { text: "Nom", value: "firstName", sortable: true },
      { text: "Prénom", value: "lastName", sortable: true },
      { text: "Email", value: "email", sortable: true },
      { text: "Rôle(s)", value: "roles", sortable: true },
      { text: "Fédérations(s)", value: "federations", sortable: true },
      { text: "Disciplines(s)", value: "disciplines", sortable: true },
      { text: "Équipes(s)", value: "teams", sortable: true },
      { text: "Actions", value: "action", sortable: false },
    ],
    users: [],
    roles: [],
    editedIndex: -1,
    editedItem: {},
    showPassword: false,
    isEditing: false,
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "Créer utilisateur" : "Modifier utilisateur";
    },
    canViewAllUsers() {
      return PermissionsService.UsersPermissionsService.canViewAllUsers;
    },
    canManageAllUsers() {
      let permission = PermissionsService.UsersPermissionsService.canManageAllUsers;
      // If we don't have the permission to manage users, we wanna remove the actions header from the datatable
      if (!permission && this.headers.find((h) => h.text == "Actions")) {
        this.headers = this.headers.filter((h) => h.text != "Actions");
      }

      return permission;
    },
  },

  watch: {
    dialog(val) {
      val || this.closeDialog();
    },
  },

  async mounted() {
    // Check if the user can see this page
    if (!this.canViewAllUsers) {
      store.dispatch("setSnackbar", {
        title: `Non Autorisé`,
        text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
        status: "warning",
        timeout: 2000,
      });

      this.$router.replace("/");
    }

    await this.initializeList();
  },

  methods: {
    // Init the list of users
    async initializeList() {
      this.users = await getUsersListAsync();
      this.roles.el = "#rolelist";
      this.roles = await getRolesListAsync();
    },

    // CREATE or UPDATE the user with form validation
    async submitForm(item) {
      var isFormValid = this.$refs.userForm.validate();

      // If the form is valid
      // AND check the passwords because the rule doesn't work so this is part of the cheat solution (see onClickOutsidePasswordField)
      if (isFormValid && (item.newPassword === item.passwordConfirm)) {
        // Set id to zero if creating
        if (!item.id) item.id = 0;

        var result = false;

        // If new password empty, null it
        if(item.newPassword === "") {
          item.newPassword = null;
          item.passwordConfirm = null;
        }

        if (item.id === 0) {
          item.id = null;
          item.password = item.newPassword;
          item.newPassword = null;
          result = await createUserAsync(item);
        } else {
          result = await updateUserAsync(item.id, item);
        }

        // If we have a result in the result, the save succeeded
        if (result && (isValidUUID(result.id) || result.id > 0)) {
          var createdOrUpdatedMessage = !this.isEditing ? "enregistré" : "mis à jour";

          store.dispatch("setSnackbar", {
            title: `Utilisateur ${createdOrUpdatedMessage}`,
            text: `L'utilisateur a bien été ${createdOrUpdatedMessage}.`,
            status: "success",
            timeout: 2000,
          });

          this.editedItem = {};
          this.$refs.userForm.reset();
          this.isEditing = false;
        }

        this.closeDialog();
        this.users = await getUsersListAsync();
      }
      // Else display form errors
      else {
        // Handle missing Role
        if (!item.roles || item.roles.length < 1) {
          store.dispatch("setSnackbar", {
            title: "Rôle requis",
            text: "Veuillez renseigner le rôle.",
            status: "error",
            timeout: 2000,
          });
        }
        // handle missing firstname
        if (!item.firstName) {
          store.dispatch("setSnackbar", {
            title: "Prénom requis",
            text: "Veuillez renseigner le prénom.",
            status: "error",
            timeout: 2000,
          });
        }
        // handle missing lastname
        if (!item.lastName) {
          store.dispatch("setSnackbar", {
            title: "Nom requis",
            text: "Veuillez renseigner le nom.",
            status: "error",
            timeout: 2000,
          });
        }
        // handle missing email
        if (!item.email) {
          store.dispatch("setSnackbar", {
            title: "Email requis",
            text: "Veuillez renseigner l'email.",
            status: "error",
            timeout: 2000,
          });
        }
        // handle wrong email
        if (item.email && !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(item.email)) {
          store.dispatch("setSnackbar", {
            title: "Email invalide",
            text: "Veuillez renseigner un email valide.",
            status: "error",
            timeout: 2000,
          });
        }
        // handle missing newPassword
        if (!item.newPassword && item.passwordConfirm) {
          store.dispatch("setSnackbar", {
            title: "Mot de passe requis",
            text: "Veuillez renseigner le mot de passe.",
            status: "error",
            timeout: 2000,
          });
        }
        // handle wrong newPassword
        if (item.newPassword && item.newPassword.length < 5) {
          store.dispatch("setSnackbar", {
            title: "Mot de passe trop court",
            text: "Veuillez renseigner un mot de passe de plus de 5 caractères.",
            status: "error",
            timeout: 2000,
          });
        }
        // handle missing newPassword confirmation
        if (item.newPassword && !item.passwordConfirm) {
          store.dispatch("setSnackbar", {
            title: "Mot de passe de confirmation requis",
            text: "Veuillez renseigner un mot de passe de confirmation.",
            status: "error",
            timeout: 2000,
          });
        }
        // handle newPassword validation
        if (item.newPassword && item.passwordConfirm && item.newPassword !== item.passwordConfirm) {
          store.dispatch("setSnackbar", {
            title: "Mots de passes différents",
            text: "Veuillez renseigner deux mots de passes identiques.",
            status: "error",
            timeout: 2000,
          });
        }
      }
    },

    // Part of the cheat solution to force the newPassword check passwordConfirm
    onClickOutsidePasswordField() {
      this.editedItem.isEnabled = !this.editedItem.isEnabled;
      this.editedItem.isEnabled = !this.editedItem.isEnabled;
    },

    // DELETE a user
    async deleteUser(e, id) {
      e.stopPropagation();

      if (confirm("Etes-vous sûr de vouloir supprimer cet utilisateur ?")) {
        var result = await deleteUserAsync(id);

        // If the result is the id of the exercise the deletion worked
        if (result && id === result.id) {
          // Refresh the exercice page with the provided parameters
          await this.initializeList();

          store.dispatch("setSnackbar", {
            title: "Utilisateur supprimé",
            text: "L'utilisateur a bien été supprimé.",
            status: "success",
            timeout: 2000,
          });
        }
      }
    },

    closeDialog() {
      this.$refs.userForm.reset();
      this.dialog = false;
    },

    openCreateUserDialog() {
      this.editedItem = {};
      this.dialog = true;
      this.showPassword = false;
    },

    openEditUserDialog(item) {
      this.isEditing = true;
      this.dialog = true;
      this.editedItem = _.cloneDeep(item);
      this.editedItem.newPassword = "";
      this.editedItem.passwordConfirm = "";
      this.showPassword = false;
    },
  },
};
</script>
